import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import Maintenance from './pages/Maintenance'
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'))
import { Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import CartDrawer from './components/CartDrawer'
const GoogleAuthLayout = lazy(() => import('./components/GoogleAuthLayout'))
import Footer from './components/Footer'
import Header from './components/Header'
const Account = lazy(() => import('./pages/Account'))
const InvoicePage = lazy(() => import('./pages/Account').then((m) => ({ default: m.InvoicePage })))
const Admin = lazy(() => import('./pages/admin/Admin'))
import { RequireAuth } from './pages/RequireAuth'
const Login = lazy(() => import('./pages/Auth').then((m) => ({ default: m.Login })))
const Register = lazy(() => import('./pages/Auth').then((m) => ({ default: m.Register })))
const ForgotPassword = lazy(() => import('./pages/Auth').then((m) => ({ default: m.ForgotPassword })))
const ResetPassword = lazy(() => import('./pages/Auth').then((m) => ({ default: m.ResetPassword })))
const AdminLogin = lazy(() => import('./pages/Auth').then((m) => ({ default: m.AdminLogin })))
const CartPage = lazy(() => import('./pages/Cart'))
const Catalogue = lazy(() => import('./pages/Catalogue'))
const Checkout = lazy(() => import('./pages/Checkout'))
const Confirmation = lazy(() => import('./pages/Confirmation'))
import Home from './pages/Home'
const ClubShop = lazy(() => import('./pages/Misc').then((m) => ({ default: m.ClubShop })))
const Clubs = lazy(() => import('./pages/Misc').then((m) => ({ default: m.Clubs })))
const Legal = lazy(() => import('./pages/Misc').then((m) => ({ default: m.Legal })))
const NotFound = lazy(() => import('./pages/Misc').then((m) => ({ default: m.NotFound })))
const Partners = lazy(() => import('./pages/Misc').then((m) => ({ default: m.Partners })))
const ServicesClubs = lazy(() => import('./pages/ServicesClubs'))
const SimulateurFlocage = lazy(() => import('./pages/SimulateurFlocage'))
const ProductPage = lazy(() => import('./pages/Product'))
const QuotePage = lazy(() => import('./pages/Quote'))
const PaymentCancel = lazy(() => import('./pages/Payment').then((m) => ({ default: m.PaymentCancel })))
const PaymentSuccess = lazy(() => import('./pages/Payment').then((m) => ({ default: m.PaymentSuccess })))
import { api } from './lib/api'

function SiteLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Suspense fallback={<div className="min-h-[60vh]" />}><Outlet /></Suspense></main>
      <Footer />
      <CartDrawer />
    </div>
  )
}

/** Redirige les anciennes URLs vers les nouvelles en se basant sur la table redirects. */
function RedirectHandler() {
  const location = useLocation()
  const navigate = useNavigate()
  const redirectsRef = useRef<{ source: string; destination: string }[]>([])
  const loaded = useRef(false)

  useEffect(() => {
    if (!loaded.current) {
      loaded.current = true
      api<{ source: string; destination: string }[]>('/redirects').then((r) => { redirectsRef.current = r }).catch(() => null)
    }
  }, [])

  useEffect(() => {
    const path = location.pathname
    const full = location.pathname + (location.search || '')
    const match = redirectsRef.current.find((r) => r.source === full || r.source === path)
    if (match) navigate(match.destination, { replace: true })
  }, [location, navigate])

  return null
}

export default function App() {
  const location = useLocation()
  const [siteOpen, setSiteOpen] = useState<boolean | null>(null)
  const isAdmin = location.pathname.startsWith('/admin')
  useEffect(() => { window.scrollTo({ top: 0 }) }, [location.pathname])
  useEffect(() => {
    // Requête lancée dès index.html (en parallèle du JavaScript) pour ne pas retarder l'affichage
    const early = (window as unknown as { __siteStatus?: Promise<{ open: boolean }> }).__siteStatus
    const req = early ?? api<{ open: boolean }>('/site-status')
    ;(window as unknown as { __siteStatus?: unknown }).__siteStatus = undefined
    req.then((s) => setSiteOpen(s.open)).catch(() => setSiteOpen(true))
  }, [isAdmin])
  if (!isAdmin && siteOpen === false) return <Maintenance />
  return (
    <>
      <RedirectHandler />
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-slate-400" data-testid="page-loading">Chargement…</div>}>
      <Routes>
        {/* Page de connexion admin — GoogleOAuthProvider inclus (RGPD) */}
        <Route element={<GoogleAuthLayout />}>
          <Route path="/admin/connexion" element={<AdminLogin />} />
        </Route>
        <Route path="/admin/:section?" element={<RequireAuth admin><Admin /></RequireAuth>} />
        <Route element={<SiteLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/sport/:sport" element={<Catalogue />} />
          <Route path="/sport/:sport/:sub" element={<Catalogue />} />
          <Route path="/:rubrique/:sub" element={<Catalogue />} />
          <Route path="/recherche/:query" element={<Catalogue />} />
          <Route path="/produit/:id" element={<ProductPage />} />
          <Route path="/panier" element={<CartPage />} />
          <Route path="/desabonnement" element={<Unsubscribe />} />
          <Route path="/commande" element={<Checkout />} />
          <Route path="/confirmation/:number" element={<Confirmation />} />
          <Route path="/devis" element={<QuotePage />} />
          <Route path="/paiement/succes" element={<PaymentSuccess />} />
          <Route path="/paiement/annule" element={<PaymentCancel />} />
          <Route path="/club" element={<Clubs />} />
          <Route path="/services-clubs" element={<ServicesClubs />} />
          <Route path="/simulateur-flocage" element={<SimulateurFlocage />} />
          <Route path="/club/:code" element={<ClubShop />} />
          <Route path="/catalogues" element={<Partners />} />
          <Route path="/cgv" element={<Legal page="cgv" />} />
          <Route path="/mentions-legales" element={<Legal page="mentions-legales" />} />
          <Route path="/confidentialite" element={<Legal page="confidentialite" />} />
          <Route path="/compte" element={<RequireAuth><Account /></RequireAuth>} />
          <Route path="/compte/facture/:number" element={<RequireAuth><InvoicePage /></RequireAuth>} />
          {/* Pages auth — GoogleOAuthProvider chargé seulement ici (RGPD) */}
          <Route element={<GoogleAuthLayout />}>
            <Route path="/connexion" element={<Login />} />
            <Route path="/inscription" element={<Register />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
            <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
    </>
  )
}
